import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { FORM_KEYS } from '@/api/useForm';

import type { FormResponse, FormResponseListResponse, SubmitResponsePayload } from '@/types/form';

const RESPONSE_KEYS = {
    all: ['form-responses'] as const,
    list: (slug: string) => [...RESPONSE_KEYS.all, 'list', slug] as const,
    detail: (slug: string, id: number | string) => [...RESPONSE_KEYS.all, 'detail', slug, String(id)] as const,
};

export { RESPONSE_KEYS as FORM_RESPONSE_KEYS };

export function useSubmitResponse(slug: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: SubmitResponsePayload) => {
            const { data } = await api.post<FormResponse>(API_ROUTES.formRespond(slug), payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.detail(slug) });
            queryClient.invalidateQueries({ queryKey: RESPONSE_KEYS.list(slug) });
        },
        onError: () => {
            toast.error('No se pudo enviar la respuesta. Revisa tu conexion e intenta de nuevo.');
        },
    });
}

export function useFormResponses(slug: string) {
    return useInfiniteQuery({
        queryKey: RESPONSE_KEYS.list(slug),
        queryFn: async ({ pageParam = 0 }) => {
            const { data } = await api.get<FormResponseListResponse>(
                API_ROUTES.formResponses(slug),
                { params: { limit: 20, offset: pageParam } }
            );
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            if (!lastPage.next) return undefined;
            try {
                const url = new URL(lastPage.next);
                const offset = url.searchParams.get('offset');
                return offset ? parseInt(offset, 10) : undefined;
            } catch {
                return undefined;
            }
        },
        enabled: !!slug,
    });
}

export function useResponseDetail(slug: string, responseId: number | string) {
    return useQuery({
        queryKey: RESPONSE_KEYS.detail(slug, responseId),
        queryFn: async () => {
            const { data } = await api.get<FormResponse>(API_ROUTES.formResponseDetail(slug, responseId));
            return data;
        },
        enabled: !!slug && !!responseId,
    });
}

export function useEditResponse(slug: string, responseId: number | string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: SubmitResponsePayload) => {
            const { data } = await api.patch<FormResponse>(
                API_ROUTES.formResponseEdit(slug, responseId),
                payload
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: RESPONSE_KEYS.detail(slug, responseId) });
            queryClient.invalidateQueries({ queryKey: RESPONSE_KEYS.list(slug) });
            toast.success('Respuesta actualizada');
        },
        onError: () => {
            toast.error('No se pudo actualizar la respuesta. Intenta de nuevo.');
        },
    });
}
